import React, {forwardRef, useState, useEffect} from 'react'
import {useClient, useFormValue} from 'sanity'
import {Stack, Text, Card} from '@sanity/ui'
import {StringInputProps} from 'sanity'

interface AlbumURLProps extends StringInputProps {
  // Add any additional props if needed
}

interface AlbumData {
  albumId?: number
  make?: string
  model?: string
  generation?: string
  diameter?: string
  design?: string
  finish?: string
}

const AlbumURL = forwardRef<HTMLInputElement, AlbumURLProps>((props, ref) => {
  const client = useClient({apiVersion: '2024-01-01'})
  const document = useFormValue([]) // Get the current document being edited
  const [url, setUrl] = useState('Loading...')

  // Get frontend URL from environment or fallback
  const baseURL = process.env.SANITY_STUDIO_FE_URL || 'http://localhost:3000'

  const _id = (document as any)?._id || ''

  useEffect(() => {
    if (!_id) {
      setUrl('N/A')
      return
    }

    client
      .fetch<AlbumData>(
        `*[_id == $id][0]{
          albumId,
          "make": vehicle.make->pageInfo.canonical.current,
          "model": vehicle.model->pageInfo.canonical.current,
          "generation": vehicle.generation->pageInfo.canonical.current,
          "diameter": diameter->slug.current,
          "design": design->{ "slug": *[_type == "wheelLanding" && references(^._id)][0].pageInfo.canonical.current }.slug,
          "finish": finish->slug.current
        }`,
        {id: _id},
      )
      .then((data) => {
        const {albumId, make, model, generation, diameter, design, finish} = data || {}

        if (!make || !model || !generation || !albumId) {
          setUrl('N/A')
          return
        }

        // Build slug: {make}-{model}-{diameter}-{design}-{finish}-{albumId}
        const slugParts = [make, model, diameter, design, finish, String(albumId)].filter(Boolean)
        const slug = slugParts.join('-')

        const albumUrl = `${baseURL}/gallery/${make}/${model}/${generation}/${slug}`
        setUrl(albumUrl)
      })
      .catch((error) => {
        console.error('Error fetching album data:', error)
        setUrl('Error loading URL')
      })
  }, [document, client, _id, baseURL])

  const isDraft = _id && _id.includes('draft')

  return (
    <Card padding={3} tone="transparent">
      <Stack gap={2}>
        <Text size={1} weight="medium">
          Album URL:
        </Text>
        {_id && !isDraft ? (
          <Text size={1}>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: '#0066cc',
                textDecoration: 'underline',
                wordBreak: 'break-all',
              }}
            >
              {url}
            </a>
          </Text>
        ) : (
          <Text size={1} muted>
            {isDraft ? 'publish album first...' : 'No album ID available'}
          </Text>
        )}
      </Stack>
    </Card>
  )
})

AlbumURL.displayName = 'AlbumURL'

export default AlbumURL
